import type { Country, Segment } from "@/types";
import type { DocType } from "./documents";

/**
 * Who cannot teach.
 *
 * A support queue, not a report. An institution blocked from starting lessons
 * is an urgent problem for somebody with a class in front of them, and the
 * queue's whole job is to separate the two reasons it happens.
 *
 * **`gaps` are ours and `outstanding` is theirs.** An institution waiting for a
 * document nobody has published cannot fix it, and a queue that mixed the two
 * would have support chasing customers about our release backlog.
 */

export interface CoverageRow {
  institutionId: string;
  name: string;
  country: Country;
  segment: Segment;
  outstanding: Array<{ docId: string; type: DocType; version: number; title: string }>;
  gaps: DocType[];
  /** A count. Group names are content, and reading content takes a grant. */
  blockedGroups: number;
  activeGroups: number;
}

export type Fault = "ours" | "theirs" | "clear";

export function faultOf(row: CoverageRow): Fault {
  // Ours first when both are true: asking an institution to accept the two
  // documents they can see, while a third is missing entirely, does not unblock
  // them and spends their patience on our problem.
  if (row.gaps.length > 0) return "ours";
  if (row.outstanding.length > 0) return "theirs";
  return "clear";
}

export const FAULT_KEY: Record<Fault, string> = {
  ours: "legal.coverage.ours",
  theirs: "legal.coverage.theirs",
  clear: "legal.coverage.clear",
};

export const FAULT_TONE: Record<Fault, string> = {
  ours: "text-accent",
  theirs: "text-cue-amber",
  clear: "text-cue-green",
};

const FAULT_ORDER: Record<Fault, number> = { ours: 0, theirs: 1, clear: 2 };

/**
 * Ours first, then by how much teaching is actually stopped.
 *
 * Sorting alphabetically would put the studio with no groups above the school
 * with eleven classes waiting, which is the wrong queue to work down.
 */
export function sortCoverage(rows: readonly CoverageRow[]): CoverageRow[] {
  return [...rows].sort(
    (a, b) =>
      FAULT_ORDER[faultOf(a)] - FAULT_ORDER[faultOf(b)] ||
      b.blockedGroups - a.blockedGroups ||
      a.name.localeCompare(b.name)
  );
}

export interface CoverageTotals {
  institutions: number;
  blockedInstitutions: number;
  blockedGroups: number;
  /** Institutions blocked by something nobody has published. */
  ours: number;
}

export function totals(rows: readonly CoverageRow[]): CoverageTotals {
  return {
    institutions: rows.length,
    blockedInstitutions: rows.filter((row) => faultOf(row) !== "clear").length,
    blockedGroups: rows.reduce((sum, row) => sum + row.blockedGroups, 0),
    ours: rows.filter((row) => faultOf(row) === "ours").length,
  };
}

/** Every document type nobody has published, across the whole estate. */
export function missingTypes(rows: readonly CoverageRow[]): DocType[] {
  const seen: DocType[] = [];
  for (const row of rows) {
    for (const type of row.gaps) if (!seen.includes(type)) seen.push(type);
  }
  return seen;
}

export function matchesQuery(row: CoverageRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return (
    row.name.toLowerCase().includes(needle) ||
    row.institutionId.toLowerCase().includes(needle) ||
    row.country.toLowerCase() === needle
  );
}
